/* eslint-disable import/prefer-default-export */
// @flow
import { call, put, takeEvery, select, spawn } from 'redux-saga/effects';
import type { Saga } from 'redux-saga';
import get from 'lodash/get';
import { flattenProducts } from '@nexus/fff-cart';

import {
  CART,
  receiveUpdateCart,
  receiveUpdateCartError,
  receiveUpdatePublicCart,
  receiveUpdatePublicCartError,
} from 'services/actions/cartActions';
import {
  toggleSoldOut,
  hideSponsoredWarning,
  receiveCartProducts,
} from 'services/actions/productsActions';
import {
  updateCart as _updateCart,
  removeVariantItemsCart,
  updatePublicCart,
  getPublicCartData,
} from 'services/api/carts';
import { createSessionId } from 'services/api/account';
import { CAMPAIGN_TYPE, getCampaignType } from 'utils/campaign';
import {
  segmentEnum,
  track,
  btnLocationEnum,
  trackAcquisitionStarted,
} from 'utils/segment';
import cookie, { COOKIE_NAMES } from 'utils/cookie';
import { selectTrackingData } from 'selectors/trackingSelector';

const {
  REQUEST_ADD_TO_CART,
  REQUEST_REMOVE_FROM_CART,
  REQUEST_CLEAR_VARIANT_ITEMS_FROM_CART,
  REQUEST_ADD_TO_PUBLIC_CART,
} = CART;

// WATCHER SAGAS
export function* watchUpdateCart(): Saga<void> {
  yield takeEvery(REQUEST_ADD_TO_CART, updateCart);
  yield takeEvery(REQUEST_REMOVE_FROM_CART, updateCart);
  yield takeEvery(
    REQUEST_CLEAR_VARIANT_ITEMS_FROM_CART,
    handleRemoveVariantItemsFromCart
  );
  yield takeEvery(REQUEST_ADD_TO_PUBLIC_CART, addToPublicCart);
}

// WORKER SAGAS
// handle side effects when adding/removing products
function* updateCart(action) {
  const {
    variantId,
    productId,
    buttonLocation,
    ctaName,
    selectedCategoryId,
    type,
    positionLocation,
    referrerProductId,
    positionOrder,
    cadenceType,
  } = action;
  try {
    const updateType = type === CART.REQUEST_ADD_TO_CART ? 'ADD' : 'REMOVE';
    const campaignType = getCampaignType();
    const isSubscribable = !!cadenceType;

    yield put(hideSponsoredWarning());
    const queryParams = {
      operation: updateType,
      campaignType,
      ...(cadenceType && { cadenceType }),
    };

    const cartResponse = yield call(_updateCart, variantId, queryParams);
    const { success, value } = cartResponse;

    if (success) {
      yield put(toggleSoldOut(variantId, isSubscribable, false));
      yield put(receiveUpdateCart(variantId, value.cartDetails));
      if (campaignType === CAMPAIGN_TYPE.shop && !isSubscribable) {
        // API response contains all the products in the cart for Shop
        const { products, variants } = flattenProducts(value.products);
        yield put(receiveCartProducts({ products, variants }));
      }
    } else {
      yield put(receiveUpdateCartError(variantId));
      yield put(toggleSoldOut(variantId, isSubscribable, true));
    }

    yield spawn(
      handleUpdateCartTracking,
      updateType,
      productId,
      variantId,
      selectedCategoryId,
      buttonLocation,
      ctaName,
      positionLocation,
      referrerProductId,
      positionOrder,
      cadenceType
    );
  } catch {
    yield put(receiveUpdateCartError(variantId));
  }
}

function* handleUpdateCartTracking(
  updateType: 'ADD' | 'REMOVE',
  productId: string,
  variantId: string,
  selectedCategoryId: string | null,
  buttonLocation: string,
  ctaName: string,
  positionLocation: string,
  referrerProductId: number | null,
  positionOrder: number,
  cadenceType: string | null
): Saga<void> {
  // track
  const trackDisplayResults =
    buttonLocation === btnLocationEnum.productCard ||
    buttonLocation === btnLocationEnum.productAccordion;
  const isRecommendedProduct =
    buttonLocation === btnLocationEnum.recommendedCard ||
    buttonLocation === btnLocationEnum.recommendedAccordion;
  const trackingDataFromState = yield select((state) =>
    selectTrackingData(
      state,
      { productId, variantId, selectedCategoryId },
      trackDisplayResults
    )
  );
  const productsDataFromState = yield select((state) => state.productsState);
  const { ratings } = productsDataFromState;
  const productRating = get(ratings, productId, null);

  const trackingData = {
    ...trackingDataFromState,
    cta_name: ctaName,
    experience: buttonLocation,
    ...(positionLocation && { position_location: positionLocation }),
    ...(isRecommendedProduct && {
      component_type: 'p2p recommendation',
      component_label: 'Similar Products',
      referrer_parent_product_id: referrerProductId,
      event_position_order: positionOrder,
    }),
    ...(productRating && {
      star_rating: productRating.averageRating,
      total_reviews: productRating.totalReviewCount,
    }),
    subscribable: !!cadenceType,
  };

  const FBTrackingData = {
    value:
      //not all products have a sale price, so we use retail price
      trackingDataFromState.sale_price || trackingDataFromState.retail_price,
    content_ids: [trackingDataFromState.product_id],
    content_type: 'product',
    currency: 'USD',
  };

  if (updateType === 'ADD') {
    track(segmentEnum.FBProductAdded, { ...trackingData, ...FBTrackingData });
    track(segmentEnum.productAdded, trackingData);
  } else {
    track(segmentEnum.productRemoved, trackingData);
  }
}

function* handleRemoveVariantItemsFromCart(action) {
  const { variantId } = action;
  try {
    const campaignType = getCampaignType();
    const queryParams = {
      campaignType,
    };
    const cartResponse = yield call(
      removeVariantItemsCart,
      variantId,
      queryParams
    );
    const { success } = cartResponse;

    if (success) {
      yield call(updateCart, { ...action, type: REQUEST_ADD_TO_CART });
    } else {
      yield put(receiveUpdateCartError(variantId));
    }
  } catch {
    yield put(receiveUpdateCartError(variantId));
  }
}

function* addToPublicCart(action) {
  const {
    variantId,
    buttonLocation,
    cadenceType,
    variantSku,
    productName,
    salePrice,
    redirectUrl,
  } = action;
  try {
    yield call(createSessionId);
    const { cartDetails } = yield call(getPublicCartData);
    if (cartDetails.variantIds.length) {
      // remove variant if cart is not empty
      const variantIdToRemove = cartDetails.variantIds[0];
      yield call(updatePublicCart, variantIdToRemove, 'REMOVE');
    }
    // add item to cart
    yield call(updatePublicCart, variantId, 'ADD', cadenceType);
    trackAcquisitionStarted({
      cta: 'ADD TO CART',
      sku: variantSku,
      productName,
      salePrice,
      acquisitionType: buttonLocation,
      userId: null,
    });
    yield put(receiveUpdatePublicCart(variantId));
    window.location.href = redirectUrl;
  } catch {
    yield put(receiveUpdatePublicCartError(variantId));
  }
}
